<template>
  <div>
    <div v-if="showLabel" class="caption period-title mb-1">{{ label }}</div>
    <div
      class="bus-picker-trigger"
      :class="{
        'bus-picker-trigger-filled': !!value,
        'bus-picker-mono': !!value && selectedTile.mono,
        'bus-picker-disabled': disabled,
      }"
      :style="value ? selectedTile.style : null"
      @click="open"
    >
      <template v-if="value">
        <div class="bus-picker-trigger-num">{{ selectedTile.main }}</div>
        <div v-if="selectedTile.sub" class="bus-picker-trigger-color">
          {{ selectedTile.sub }}
        </div>
        <div v-if="selectedTile.weekday" class="bus-picker-trigger-day">
          {{ selectedTile.weekday }}
        </div>
        <div v-if="selectedGroupLabel" class="bus-picker-trigger-extra">
          {{ selectedGroupLabel }}
        </div>
      </template>
      <template v-else>
        <v-icon class="mb-1">mdi-bus</v-icon>
        <div class="bus-picker-trigger-empty">
          {{ loading ? "Loading buses…" : "Select " + label }}
        </div>
      </template>
      <v-btn
        v-if="value && !disabled"
        icon
        small
        class="bus-picker-clear ma-0"
        :style="{ color: selectedTile.style.color }"
        :title="'Clear ' + label.toLowerCase()"
        @click.stop="$emit('clear')"
      >
        <v-icon small :style="{ color: selectedTile.style.color }"
          >mdi-close</v-icon
        >
      </v-btn>
    </div>

    <v-dialog v-model="dialog" max-width="760" scrollable>
      <v-card>
        <v-card-title class="dialog-header py-2">
          <v-icon class="mr-3 pt-1" color="white" size="24">mdi-bus</v-icon>
          <span class="dialog-title">Select {{ label }}</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text my-0" icon dark @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-3 bus-picker-dialog-body">
          <div v-if="loading" class="text-xs-center py-4 grey--text">
            Loading buses…
          </div>
          <template v-else>
            <div
              v-if="!hasMainChoices && !listMode"
              class="text-xs-center py-4 grey--text"
            >
              No buses available for this student.
            </div>
            <div
              v-for="(section, index) in mainSections"
              :key="'s-' + section.key"
              :class="{ 'mt-3': index > 0 }"
            >
              <div v-if="section.title" class="bus-picker-extra-title mb-2">
                {{ section.title }}
              </div>
              <div
                class="bus-picker-grid"
                :class="{ 'bus-picker-grid-small': compact }"
              >
                <button
                  v-for="item in section.choices"
                  :key="item.key"
                  type="button"
                  class="bus-picker-btn"
                  :class="{
                    'bus-picker-btn-selected': item.selected,
                    'bus-picker-mono': item.tile.mono,
                  }"
                  :style="item.tile.style"
                  @click="choose(item.route)"
                >
                  <v-icon
                    v-if="item.selected"
                    class="bus-picker-btn-check"
                    small
                  >
                    mdi-check-circle
                  </v-icon>
                  <div class="bus-picker-btn-num">{{ item.tile.main }}</div>
                  <div v-if="item.tile.sub" class="bus-picker-btn-color">
                    {{ item.tile.sub }}
                  </div>
                  <div v-if="item.tile.time" class="bus-picker-btn-time">
                    {{ item.tile.time }}
                  </div>
                  <div v-if="item.tile.weekday" class="bus-picker-btn-day">
                    {{ item.tile.weekday }}
                  </div>
                </button>
              </div>
            </div>

            <div v-if="availableGroups.length" class="bus-picker-extra-bar mt-3">
              <v-btn
                v-if="!listMode"
                small
                outline
                class="ml-0 my-1"
                :color="showExtra ? 'success' : 'grey darken-1'"
                @click="showExtra = !showExtra"
              >
                <v-icon left small>{{
                  showExtra ? "mdi-chevron-up" : "mdi-bus"
                }}</v-icon>
                {{ showExtra ? "Hide additional bus routes" : "Show additional bus routes" }}
                <span class="ml-1 grey--text">({{ extraRouteCount }})</span>
              </v-btn>

              <div v-if="showExtra || listMode" class="bus-picker-extra-panel mt-2">
                <div class="bus-picker-chip-row">
                  <button
                    v-for="g in extraTabs"
                    :key="'t-' + g.key"
                    type="button"
                    class="bus-picker-chip"
                    :class="{ 'bus-picker-chip-on': activeGroup && g.key === activeGroup.key }"
                    @click="activeGroupKey = g.key"
                  >
                    {{ g.label || g.title }}
                    <span class="bus-picker-chip-count">{{ g.count }}</span>
                  </button>
                </div>
                <div v-if="value" class="bus-picker-current">
                  <span class="bus-picker-current-label">Selected</span>
                  <span
                    class="bus-picker-current-chip"
                    :class="{ 'bus-picker-mono': selectedTile.mono }"
                    :style="selectedTile.style"
                  >
                    {{ selectedTile.main }}
                  </span>
                  <span v-if="selectedTile.sub" class="bus-picker-current-text">{{
                    selectedTile.sub
                  }}</span>
                  <span
                    v-if="selectedTile.time && !listMode"
                    class="bus-picker-current-text"
                    >{{ selectedTile.time }}</span
                  >
                  <span v-if="selectedTile.weekday" class="bus-picker-day-chip">{{
                    selectedTile.weekday
                  }}</span>
                </div>
                <v-text-field
                  v-model="extraSearch"
                  placeholder="Search bus #, room, grade, color, day…"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  single-line
                  hide-details
                  class="mt-0 pt-0 mb-2"
                ></v-text-field>
                <div class="bus-picker-extra-list">
                  <div
                    v-if="!activeGroup || !activeGroup.choices.length"
                    class="text-xs-center grey--text py-3"
                  >
                    No matching buses.
                  </div>
                  <template v-if="activeGroup">
                   <template v-for="tg in activeTimeGroups">
                    <div
                      v-if="!listMode"
                      :key="'th-' + tg.key"
                      class="bus-picker-list-head"
                    >
                      {{ tg.time || "No start time" }}
                      <span class="grey--text">({{ tg.choices.length }})</span>
                    </div>
                    <div
                      v-for="item in tg.choices"
                      :key="'r-' + item.key"
                      class="bus-picker-row"
                      :class="{ 'bus-picker-row-selected': item.selected }"
                      @click="choose(item.route)"
                    >
                      <span
                        class="bus-picker-swatch"
                        :style="{
                          backgroundColor: item.tile.style.backgroundColor,
                          borderColor: item.tile.style.borderColor,
                        }"
                      ></span>
                      <span class="bus-picker-row-main">{{ item.tile.main }}</span>
                      <span v-if="item.tile.sub" class="bus-picker-row-sub">{{
                        item.tile.sub
                      }}</span>
                      <span v-if="routeGrades(item.route)" class="bus-picker-row-grades">{{
                        routeGrades(item.route)
                      }}</span>
                      <v-spacer></v-spacer>
                      <span
                        v-if="item.tile.time && !listMode"
                        class="bus-picker-row-time"
                        >{{ item.tile.time }}</span
                      >
                      <span v-if="item.tile.weekday" class="bus-picker-day-chip">{{
                        item.tile.weekday
                      }}</span>
                      <v-icon v-if="item.selected" color="success"
                        >mdi-check-circle</v-icon
                      >
                    </div>
                   </template>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  applyBuildingIdentity,
  buildRouteChoices,
  getRouteTile,
} from "../busRouteDisplay";

// "3:30 PM" / "15:30" → minutes since midnight; unknown times sort last.
function timeSortKey(text) {
  const m = String(text || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!m) return 99999;
  let h = parseInt(m[1], 10);
  const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return h * 60 + parseInt(m[2], 10);
}

export default {
  name: "BusRoutePicker",
  props: {
    value: { type: Object, default: null },
    label: { type: String, required: true },
    showLabel: { type: Boolean, default: true },
    routes: { type: Array, default: () => [] },
    // [{ key, title, routes }] shown behind "Show additional bus routes"
    extraGroups: { type: Array, default: () => [] },
    // Optional titled main sections [{ key, title, routes }]; replaces `routes`.
    sections: { type: Array, default: null },
    // Small tiles for the main sections too (long lists like the special-day picker).
    compact: { type: Boolean, default: true },
    // Show `sections` as building/grade buttons plus a searchable list (no tiles).
    listMode: { type: Boolean, default: false },
    colorMap: { type: Object, default: () => ({}) },
    building: { type: String, default: "" },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data: () => ({
    dialog: false,
    showExtra: false,
    extraSearch: "",
    activeGroupKey: null,
  }),
  computed: {
    selectedId() {
      return this.value ? this.value.id : null;
    },
    selectedTile() {
      return getRouteTile(
        applyBuildingIdentity(this.value, this.building),
        this.colorMap
      );
    },
    mainRoutes() {
      if (!this.sections) return this.routes || [];
      return [].concat(...this.sections.map((s) => s.routes || []));
    },
    mainSections() {
      if (this.listMode) return [];
      const sections = this.sections || [
        { key: "main", title: "", routes: this.routes },
      ];
      return sections
        .map((s) =>
          Object.assign({}, s, {
            choices: buildRouteChoices(
              this.routesForBuilding(s.routes),
              this.selectedId,
              this.colorMap
            ),
          })
        )
        .filter((s) => s.choices.length);
    },
    hasMainChoices() {
      return this.mainSections.length > 0;
    },
    availableGroups() {
      const source = this.listMode ? this.sections : this.extraGroups;
      return (source || []).filter((g) => g && g.routes && g.routes.length);
    },
    extraRouteCount() {
      const ids = {};
      this.availableGroups.forEach((g) =>
        g.routes.forEach((r) => {
          ids[r.id] = true;
        })
      );
      return Object.keys(ids).length;
    },
    shownGroups() {
      if (!this.showExtra && !this.listMode) return [];
      return this.availableGroups.map((g) =>
          Object.assign({}, g, {
            choices: buildRouteChoices(
              this.routesForBuilding(g.routes),
              this.selectedId,
              this.colorMap
            ),
          })
        );
    },
    filteredExtraGroups() {
      const q = (this.extraSearch || "").trim().toLowerCase();
      if (!q) return this.shownGroups;
      return this.shownGroups
        .map((g) =>
          Object.assign({}, g, {
            choices: g.choices.filter((c) =>
              [c.tile.main, c.tile.sub, c.tile.time, c.tile.weekday, this.routeGrades(c.route), g.title]
                .join(" ")
                .toLowerCase()
                .includes(q)
            ),
          })
        )
        .filter((g) => g.choices.length);
    },
    // One tab per group with its (search-filtered) route count.
    extraTabs() {
      return this.filteredExtraGroups.map((g) => ({
        key: g.key,
        title: g.title,
        label: g.label,
        count: g.choices.length,
      }));
    },
    activeGroup() {
      const groups = this.filteredExtraGroups;
      return (
        groups.find((g) => g.key === this.activeGroupKey) || groups[0] || null
      );
    },
    // Active group's routes split by start time, earliest first.
    activeTimeGroups() {
      if (!this.activeGroup) return [];
      const byTime = {};
      this.activeGroup.choices.forEach((c) => {
        const t = c.tile.time || "";
        (byTime[t] = byTime[t] || []).push(c);
      });
      return Object.keys(byTime)
        .sort((a, b) => timeSortKey(a) - timeSortKey(b))
        .map((t) => ({ key: t || "none", time: t, choices: byTime[t] }));
    },
    selectedGroup() {
      if (!this.value) return null;
      if (
        !this.listMode &&
        this.mainRoutes.some((r) => r.id === this.value.id)
      )
        return null;
      return (
        this.availableGroups.find((g) =>
          g.routes.some((r) => r.id === this.value.id)
        ) || null
      );
    },
    selectedGroupLabel() {
      if (this.listMode) return "";
      return this.selectedGroup
        ? this.selectedGroup.shortLabel || this.selectedGroup.title
        : "";
    },
  },
  methods: {
    routesForBuilding(routes) {
      return (routes || []).map((route) =>
        applyBuildingIdentity(route, this.building)
      );
    },
    open() {
      if (this.disabled) return;
      this.showExtra = !!this.selectedGroup;
      this.extraSearch = "";
      this.activeGroupKey = this.selectedGroup ? this.selectedGroup.key : null;
      this.dialog = true;
    },
    routeGrades(route) {
      return String((route && route.grades) || "")
        .split(",")
        .map((g) => g.trim())
        .filter(Boolean)
        .join(", ");
    },
    choose(route) {
      this.dialog = false;
      this.$emit("select", route);
    },
  },
};
</script>

<style scoped>
.period-title {
  color: #5f5f5f;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.dialog-header {
  background: linear-gradient(135deg, rgb(92, 184, 96) 0%, #388e3c 100%);
  color: white;
  padding: 0px 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 400;
}

.bus-picker-trigger {
  position: relative;
  min-height: 84px;
  border: 2px dashed #c5d6c5;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f7faf7;
  padding: 12px 28px 12px 12px;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.bus-picker-trigger:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.bus-picker-trigger-filled {
  border-style: solid;
}

.bus-picker-disabled {
  cursor: default;
  opacity: 0.7;
}

.bus-picker-trigger-num {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.1;
}

.bus-picker-trigger-color {
  font-size: 14px;
  font-weight: 600;
  margin-top: 2px;
  text-transform: capitalize;
}

.bus-picker-trigger-day {
  font-size: 12px;
  font-weight: 700;
  margin-top: 2px;
}

.bus-picker-trigger-extra {
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
  opacity: 0.85;
}

.bus-picker-trigger-empty {
  font-size: 14px;
  font-weight: 600;
  color: #616161;
}

.bus-picker-clear {
  position: absolute !important;
  top: 6px;
  right: 6px;
  background: rgba(255, 255, 255, 0.22) !important;
  transition: background 0.15s ease;
}

.bus-picker-clear:hover {
  background: rgba(255, 255, 255, 0.38) !important;
}

.bus-picker-dialog-body {
  background: #f4f6f4;
}

.bus-picker-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 14px;
}

.bus-picker-btn {
  position: relative;
  min-height: 112px;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
  padding: 14px 10px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.bus-picker-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.bus-picker-btn:active {
  transform: translateY(-1px);
}

.bus-picker-mono {
  border: 2px solid #111111 !important;
}

.bus-picker-btn-selected {
  box-shadow: 0 0 0 3px #ffffff, 0 0 0 6px #1b5e20,
    0 6px 16px rgba(0, 0, 0, 0.24);
}

.bus-picker-btn-check {
  position: absolute;
  top: 8px;
  right: 8px;
  color: inherit !important;
}

.bus-picker-btn-num {
  font-size: 28px;
  line-height: 1.1;
  font-weight: 800;
}

.bus-picker-btn-color {
  font-size: 15px;
  margin-top: 6px;
  font-weight: 600;
  text-transform: capitalize;
  opacity: 0.92;
}

.bus-picker-btn-time {
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
  opacity: 0.8;
}

.bus-picker-btn-day {
  font-size: 12px;
  margin-top: 2px;
  font-weight: 800;
}

.bus-picker-current {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
  padding: 8px 10px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 8px;
}

.bus-picker-current-label {
  font-size: 12px;
  font-weight: 700;
  color: #1b5e20;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.bus-picker-current-chip {
  font-size: 16px;
  font-weight: 800;
  padding: 3px 14px;
  border-radius: 10px;
  border: 2px solid transparent;
}

.bus-picker-current-text {
  font-size: 13px;
  color: #424242;
}

.bus-picker-grid-small {
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: 10px;
}

.bus-picker-grid-small .bus-picker-btn {
  min-height: 72px;
  border-radius: 12px;
  padding: 8px 6px;
}

.bus-picker-grid-small .bus-picker-btn-num {
  font-size: 19px;
}

.bus-picker-grid-small .bus-picker-btn-color {
  font-size: 12px;
  margin-top: 3px;
}

.bus-picker-grid-small .bus-picker-btn-time {
  font-size: 11px;
  margin-top: 2px;
}

.bus-picker-grid-small .bus-picker-btn-day {
  font-size: 11px;
  margin-top: 1px;
}

.bus-picker-grid-small .bus-picker-btn-check {
  top: 4px;
  right: 4px;
}

.bus-picker-extra-panel {
  background: #fff;
  border: 1px solid #dfe5df;
  border-radius: 8px;
  padding: 8px 10px;
}

.bus-picker-extra-list {
  max-height: 340px;
  overflow-y: auto;
}

.bus-picker-list-head {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f1f7f1;
  color: #388e3c;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 8px;
  margin-top: 4px;
  border-radius: 4px;
}

.bus-picker-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.bus-picker-chip {
  border: 1px solid #c5d6c5;
  background: #fff;
  color: #2e7d32;
  border-radius: 18px;
  padding: 6px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.bus-picker-chip:hover {
  background: #f1f7f1;
}

.bus-picker-chip-on {
  background: #388e3c;
  border-color: #388e3c;
  color: #fff;
}

.bus-picker-chip-on:hover {
  background: #388e3c;
}

.bus-picker-chip-count {
  margin-left: 6px;
  font-size: 12px;
  opacity: 0.85;
}

.bus-picker-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #eef1ee;
}

.bus-picker-row:hover {
  background: #f4f9f4;
}

.bus-picker-row-selected {
  background: #e8f5e9;
}

.bus-picker-swatch {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #111;
}

.bus-picker-row-main {
  font-weight: 700;
  font-size: 17px;
}

.bus-picker-row-grades {
  font-size: 13px;
  color: #2e7d32;
  font-weight: 600;
}

.bus-picker-row-sub {
  font-size: 14px;
  color: #616161;
}

.bus-picker-row-time {
  font-size: 14px;
  color: #424242;
}

.bus-picker-day-chip {
  font-size: 12px;
  font-weight: 700;
  background: #fff3e0;
  color: #e65100;
  border-radius: 10px;
  padding: 1px 8px;
}

.bus-picker-extra-bar {
  border-top: 1px solid #dfe5df;
  padding-top: 8px;
}

.bus-picker-extra-title {
  font-size: 14px;
  font-weight: 700;
  color: #388e3c;
}
</style>
